import React, { useEffect, useState, useRef } from 'react';

import { PlayFilledIcon, PauseIcon } from '@vlabs/icons';
import { BboxRenderer } from '@vlabs/monokit/shared/ui/canvas';
import type { TBbox, TOriginSize } from '@vlabs/monokit/shared/ui/canvas';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { Control } from '@vlabs/uikit';

import { findNearestBBox } from './utils';
import type { BBoxesByTime } from './utils';
import st from './VideoChecksProcessingVideo.module.sass';
import { VideoControl } from './VideoControl';
import { Timeline } from '../../../components';
import { formatTime } from '../../../utils/formatTime';
import { useVideoFromQuery } from '../useVideoFromQuery';

export interface VideoChecksProcessingResult {
  duration: number;
  tracks: unknown;
  boundingBoxes: BBoxesByTime;
}

interface VideoChecksProcessingVideoProps {
  currentTime: number;
  onEventIdClick?: (eventId: string) => void;
  onTimeChange?: (time: number) => void;
  result?: VideoChecksProcessingResult;
  selectedEventId?: string | null;
  hoveredEventId?: string | null;
  onPauseVideo?: () => void;
  onPlayVideo?: () => void;
  onEventHover?: (eventId: string | null) => void;
}

function VideoChecksProcessingVideo({
  currentTime,
  onEventIdClick = undefined,
  onTimeChange = undefined,
  result = undefined,
  selectedEventId = undefined,
  hoveredEventId = undefined,
  onPauseVideo = undefined,
  onPlayVideo = undefined,
  onEventHover = undefined,
}: VideoChecksProcessingVideoProps): React.JSX.Element | null {
  const { videoSrc, state: videoState, fileList } = useVideoFromQuery();

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [mediaOriginSize, setMediaOriginSize] = useState<TOriginSize | undefined>({ width: 0, height: 0 });
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [visibleBoxes, setVisibleBoxes] = useState<TBbox[] | undefined>(undefined);

  useEffect(() => {
    if (!result) return;

    const nearestBBoxes = findNearestBBox(result?.boundingBoxes, currentTime);

    setVisibleBoxes(nearestBBoxes.map((box) => ({
      ...box,
      isSelected: String(box.event_id) === selectedEventId,
    })));
  }, [currentTime, result, selectedEventId]);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const time = e.currentTarget.currentTime;
    onTimeChange?.(time);
  };

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (v?.videoWidth && v?.videoHeight) {
      setMediaOriginSize({ width: v.videoWidth, height: v.videoHeight });
    }
  };

  // canvas должен совпадать с видимым кадром
  const videoAspectRatio = mediaOriginSize?.width && mediaOriginSize.height
    ? mediaOriginSize.width / mediaOriginSize.height
    : undefined;

  const handleBBoxClick = (bboxList: TBbox[] | null) => {
    if (bboxList === null) return;
    const v = videoRef.current;
    if (!v?.paused) return;

    const eventId = bboxList[0]?.event_id;
    if (eventId === undefined || eventId === null) return;

    onEventIdClick?.(String(eventId));
  };

  const handleBBoxHover = (bbox: TBbox | null) => {
    const eventId = bbox?.event_id;
    onEventHover?.(eventId === undefined ? null : String(eventId));
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      v.play();
      onPlayVideo?.();
      setIsPlaying(true);
    } else {
      v.pause();
      onPauseVideo?.();
      setIsPlaying(false);
    }
  };

  const updateVideoCurrentTime = (time: number) => {
    const v = videoRef.current;
    if (!v || !result?.duration) return;

    if (!v.paused) {
      v.pause();
      setIsPlaying(false);
    }
    onPauseVideo?.();

    v.currentTime = time;
    onTimeChange?.(time);
  };

  return (
    <div className={st.Root}>
      <div className={st.VideoContent}>
        <div className={st.Canvas}>
          {videoState === 'fetched' && (
            <div className={st.FF} style={{ aspectRatio: videoAspectRatio }}>
              <video
                className={st.Video}
                onLoadedMetadata={onLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                ref={videoRef}
                src={videoSrc}
              />
              <BboxRenderer
                bboxList={visibleBoxes}
                dimOutsideSelected
                hoveredBboxId={hoveredEventId}
                onBBoxClick={handleBBoxClick}
                onBBoxHover={handleBBoxHover}
                originSize={mediaOriginSize}
              />
            </div>
          )}
          {videoState !== 'fetched' && (
            <div className={st.Loader}>
              <Loader />
            </div>
          )}
        </div>
        {/* FIXME: сделать нормальную передачу пропсов */}
        {videoState === 'fetched' && <VideoControl taskData={{ fileList, parameters: result?.parameters }} />}
      </div>

      {videoState === 'fetched' && (
        <div className={st.TimeLineContent}>
          <div className={st.ActionPanel}>
            <div className={st.ActionContent}>
              <Control.IconButton
                icon={isPlaying ? <PauseIcon /> : <PlayFilledIcon />}
                onClick={togglePlay}
              />
              <span className={st.ActionCurrentTime}>{formatTime(currentTime)}</span>
              <span className={st.ActionDivider} />
              <span className={st.ActionDuration}>{formatTime(result?.duration)}</span>
            </div>
          </div>
          <div className={st.TimelineWrapper}>
            <Timeline
              currentTime={currentTime}
              duration={result?.duration}
              onChange={updateVideoCurrentTime}
              tracks={result?.tracks}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { VideoChecksProcessingVideo };
